import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../shared/api/api.js';
import { type BlogCategory } from '../api/blogApi.js';
import { 
  Plus, 
  Search, 
  Edit3, 
  Trash2, 
  Tag, 
  Layers, 
  Check, 
  X, 
  AlertCircle,
  FileText,
  Sparkles,
  Info
} from 'lucide-react';
import { ConfirmDeleteModal } from '../../../shared/components/ConfirmDeleteModal.js';
import { useNotificationStore } from '../../notifications/store/useNotificationStore.js';

export const CATEGORY_COLORS: Record<string, { label: string; badge: string; dot: string; ring: string }> = {
  indigo: { label: 'Indigo', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500', ring: 'ring-indigo-500' },
  emerald: { label: 'Emerald Hijau', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', ring: 'ring-emerald-500' },
  amber: { label: 'Amber Emas', badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500', ring: 'ring-amber-500' },
  blue: { label: 'Biru Langit', badge: 'bg-sky-50 text-sky-700 border-sky-200', dot: 'bg-sky-500', ring: 'ring-sky-500' },
  purple: { label: 'Ungu Elegan', badge: 'bg-purple-50 text-purple-700 border-purple-200', dot: 'bg-purple-500', ring: 'ring-purple-500' },
  rose: { label: 'Rose Merah', badge: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500', ring: 'ring-rose-500' },
  cyan: { label: 'Cyan Toska', badge: 'bg-cyan-50 text-cyan-700 border-cyan-200', dot: 'bg-cyan-500', ring: 'ring-cyan-500' },
  slate: { label: 'Slate Netral', badge: 'bg-slate-100 text-slate-700 border-slate-300', dot: 'bg-slate-500', ring: 'ring-slate-500' },
};

export const getCategoryBadgeStyle = (color?: string | null) => {
  const c = color ? color.toLowerCase() : 'indigo';
  return CATEGORY_COLORS[c]?.badge || CATEGORY_COLORS.indigo.badge;
};

export const getCategoryDotStyle = (color?: string | null) => {
  const c = color ? color.toLowerCase() : 'indigo';
  return CATEGORY_COLORS[c]?.dot || CATEGORY_COLORS.indigo.dot;
};

interface CategoryModalProps {
  category: BlogCategory | null;
  isOpen: boolean;
  onClose: () => void;
}

const CategoryModal: React.FC<CategoryModalProps> = ({ category, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const showToast = useNotificationStore((state) => state.showToast);

  const [name, setName] = useState(category?.name || '');
  const [slug, setSlug] = useState(category?.slug || '');
  const [description, setDescription] = useState(category?.description || '');
  const [color, setColor] = useState(category?.color || 'indigo');
  const [isCustomSlug, setIsCustomSlug] = useState(Boolean(category));

  React.useEffect(() => {
    if (category) {
      setName(category.name);
      setSlug(category.slug);
      setDescription(category.description || '');
      setColor(category.color || 'indigo');
      setIsCustomSlug(true);
    } else {
      setName('');
      setSlug('');
      setDescription('');
      setColor('indigo');
      setIsCustomSlug(false);
    }
  }, [category, isOpen]);

  const handleNameChange = (val: string) => {
    setName(val);
    if (!isCustomSlug) {
      const generatedSlug = val
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]+/g, '')
        .replace(/--+/g, '-');
      setSlug(generatedSlug);
    }
  };

  const createMutation = useMutation({
    mutationFn: (data: { name: string; slug?: string; description?: string; color?: string }) =>
      api.createBlogCategory(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['blog-categories'] });
      showToast('Kategori Dibuat', `Kategori "${res.name}" berhasil ditambahkan.`, 'success');
      onClose();
    },
    onError: (err: any) => {
      showToast('Gagal Membuat Kategori', err.message || 'Terjadi kesalahan sistem', 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; payload: { name: string; slug?: string; description?: string; color?: string } }) =>
      api.updateBlogCategory(data.id, data.payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['blog-categories'] });
      queryClient.invalidateQueries({ queryKey: ['admin-blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      showToast('Kategori Diperbarui', `Kategori "${res.name}" berhasil diperbarui.`, 'success');
      onClose();
    },
    onError: (err: any) => {
      showToast('Gagal Memperbarui Kategori', err.message || 'Terjadi kesalahan sistem', 'error');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      showToast('Form Belum Lengkap', 'Nama kategori wajib diisi.', 'warning');
      return;
    }

    const payload = {
      name: name.trim(),
      slug: slug.trim() || undefined,
      description: description.trim() || undefined,
      color: color || 'indigo',
    };

    if (category) {
      updateMutation.mutate({ id: category.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  if (!isOpen) return null;

  const isPending = createMutation.isPending || updateMutation.isPending;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200 overflow-y-auto">
      <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl border border-slate-100 overflow-hidden my-auto flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between shrink-0 bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center shadow-inner">
              <Tag className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">
                {category ? 'Edit Kategori Artikel' : 'Tambah Kategori Baru'}
              </h3>
              <p className="text-xs text-slate-500">Klasifikasikan artikel blog ke dalam topik yang terarah.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto">
          {/* Category Name */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
              Nama Kategori <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              required
              placeholder="Contoh: Tips Kepenulisan, UIBN & Regulasi..."
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all"
            />
          </div>

          {/* Slug */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                Slug URL / Identifier
              </label>
              {!isCustomSlug && (
                <span className="text-[11px] text-slate-400 font-mono">Dibuat otomatis</span>
              )}
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="tips-kepenulisan"
                value={slug}
                onChange={(e) => {
                  setIsCustomSlug(true);
                  setSlug(e.target.value);
                }}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-mono text-slate-700 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
              Deskripsi Singkat (Opsional)
            </label>
            <textarea
              rows={2}
              placeholder="Jelaskan cakupan topik atau materi dalam kategori ini..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all resize-none"
            />
          </div>

          {/* Color & Theme Selection */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2.5">
              Warna Label & Aksen Badge
            </label>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(CATEGORY_COLORS).map(([key, item]) => {
                const isSelected = color === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setColor(key)}
                    className={`px-3 py-2 rounded-xl border text-xs font-medium flex items-center justify-between gap-1.5 transition-all cursor-pointer ${
                      isSelected 
                        ? 'border-indigo-600 bg-indigo-50/50 shadow-xs ring-2 ring-indigo-500/20 text-indigo-900 font-bold' 
                        : 'border-slate-200 hover:border-slate-300 bg-white text-slate-600'
                    }`}
                  >
                    <span className="flex items-center gap-1.5 truncate">
                      <span className={`w-2.5 h-2.5 rounded-full ${item.dot} shrink-0`} />
                      <span className="truncate">{item.label.split(' ')[0]}</span>
                    </span>
                    {isSelected && <Check className="w-3.5 h-3.5 text-indigo-600 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Live Preview */}
          <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-between">
            <span className="text-xs text-slate-500 font-medium">Pratinjau Badge:</span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-bold ${getCategoryBadgeStyle(color)}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${getCategoryDotStyle(color)}`} />
              {name.trim() || 'Nama Kategori'}
            </span>
          </div>

          {/* Actions */}
          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-colors"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-5 py-2.5 rounded-xl bg-indigo-600 text-white font-bold text-sm shadow-lg shadow-indigo-500/20 hover:bg-indigo-700 transition-all flex items-center gap-2"
            >
              {isPending ? 'Menyimpan...' : category ? 'Simpan Perubahan' : 'Buat Kategori'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export const CategoryManager: React.FC = () => {
  const queryClient = useQueryClient();
  const showToast = useNotificationStore((state) => state.showToast);

  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<BlogCategory | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<BlogCategory | null>(null);

  const { data: categories, isLoading } = useQuery({
    queryKey: ['blog-categories'],
    queryFn: () => api.getBlogCategories(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteBlogCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-categories'] });
      queryClient.invalidateQueries({ queryKey: ['admin-blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      showToast('Kategori Dihapus', 'Kategori artikel berhasil dihapus dari database.', 'success');
      setDeletingCategory(null);
    },
    onError: (err: any) => {
      showToast('Gagal Menghapus Kategori', err.message || 'Terjadi kesalahan sistem', 'error');
      setDeletingCategory(null);
    },
  });

  const filteredCategories = React.useMemo(() => {
    if (!categories) return [];
    if (!search.trim()) return categories;
    const q = search.toLowerCase();
    return categories.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.slug.toLowerCase().includes(q) ||
        (c.description && c.description.toLowerCase().includes(q))
    );
  }, [categories, search]);

  const openCreateModal = () => {
    setEditingCategory(null);
    setIsModalOpen(true);
  };

  const openEditModal = (cat: BlogCategory) => {
    setEditingCategory(cat);
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Sub Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center shadow-inner">
            <Layers className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Kelola Kategori Artikel</h2>
            <p className="text-sm text-slate-500">
              Atur taksonomi dan klasifikasi topik artikel blog untuk navigasi pembaca yang rapi.
            </p>
          </div>
        </div>
        <button
          onClick={openCreateModal}
          className="px-5 py-2.5 rounded-xl bg-indigo-600 text-white font-bold text-sm shadow-lg shadow-indigo-500/20 hover:bg-indigo-700 transition-all flex items-center gap-2 cursor-pointer shrink-0"
        >
          <Plus className="w-4 h-4" />
          Tambah Kategori
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          placeholder="Cari kategori artikel..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all shadow-xs"
        />
      </div>

      {/* Categories Table / Card Grid */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  Kategori
                </th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  Slug Identifier
                </th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  Deskripsi
                </th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">
                  Jumlah Artikel
                </th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right">
                  Aksi
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {isLoading ? (
                [1, 2, 3, 4].map((i) => (
                  <tr key={`cat-skel-${i}`} className="animate-pulse">
                    <td className="px-6 py-5" colSpan={5}>
                      <div className="h-4 bg-slate-100 rounded w-full" />
                    </td>
                  </tr>
                ))
              ) : filteredCategories.length > 0 ? (
                filteredCategories.map((cat) => (
                  <tr key={cat.id} className="hover:bg-slate-50/80 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-bold ${getCategoryBadgeStyle(
                            cat.color
                          )}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${getCategoryDotStyle(cat.color)}`} />
                          {cat.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-md">
                        {cat.slug}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-xs text-slate-600 max-w-sm line-clamp-2">
                        {cat.description || <span className="text-slate-300 italic">Tidak ada deskripsi</span>}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-bold">
                        <FileText className="w-3 h-3 text-slate-400" />
                        {cat.postCount || 0} artikel
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => openEditModal(cat)}
                          className="p-2 rounded-xl hover:bg-indigo-50 hover:text-indigo-600 text-slate-400 border border-transparent hover:border-indigo-100 transition-all cursor-pointer"
                          title="Edit Kategori"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeletingCategory(cat)}
                          className="p-2 rounded-xl hover:bg-rose-50 hover:text-rose-600 text-slate-400 border border-transparent hover:border-rose-100 transition-all cursor-pointer"
                          title="Hapus Kategori"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-300">
                        <Tag className="w-7 h-7" />
                      </div>
                      <p className="text-slate-500 text-sm font-medium">
                        {search ? 'Tidak ada kategori yang cocok dengan pencarian.' : 'Belum ada kategori artikel.'}
                      </p>
                      <button
                        onClick={openCreateModal}
                        className="text-indigo-600 text-sm font-bold hover:underline"
                      >
                        Tambah Kategori Pertama
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Category Create/Edit Modal */}
      {isModalOpen && (
        <CategoryModal
          category={editingCategory}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      {/* Confirm Delete Category Modal */}
      <ConfirmDeleteModal
        isOpen={Boolean(deletingCategory)}
        title="Hapus Kategori Artikel"
        description={
          deletingCategory
            ? `Apakah Anda yakin ingin menghapus kategori "${deletingCategory.name}"? Artikel yang menggunakan kategori ini tidak akan dihapus, tetapi status kategorinya akan diatur ulang menjadi tanpa kategori.`
            : ''
        }
        confirmText="Hapus Kategori"
        isPending={deleteMutation.isPending}
        onClose={() => setDeletingCategory(null)}
        onConfirm={() => {
          if (deletingCategory) {
            deleteMutation.mutate(deletingCategory.id);
          }
        }}
      />
    </div>
  );
};
